import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FaExclamationTriangle, FaLock, FaSearch, FaTimes, FaTimesCircle } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import api from "../services/api";
import "../components/admin/admin.css";

const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const formatHoursMins = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours}h ${mins}m`;
};

const formatDuration = (totalMinutes) => {
  if (totalMinutes === null || totalMinutes === undefined) return "";
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

const PublicStats = () => {
  const { token } = useParams();
  const [isValid, setIsValid] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [absentDates, setAbsentDates] = useState([]);
  const [showStatsModal, setShowStatsModal] = useState(false);

  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());

  useEffect(() => {
    const verifyAndLoad = async () => {
      try {
        setLoading(true);
        const verifyRes = await api.get(`/public/verify_link?token=${token}`);
        if (verifyRes.status === 200) {
          setIsValid(true);
          const empRes = await api.get(`/public/get_employees_public?token=${token}`);
          if (empRes.status === 200) {
            setEmployees(empRes.data.employees || []);
          }
        }
      } catch (error) {
        setIsValid(false);
        setErrorMsg(error.response?.data?.message || "Invalid or expired link");
      } finally {
        setLoading(false);
      }
    };
    verifyAndLoad();
  }, [token]);

  const handleEmployeeClick = async (employee) => {
    setSelectedEmployee(employee);
    setShowStatsModal(true);
    setStatsLoading(true);
    setStatsData(null);
    setAbsentDates([]);

    try {
      const response = await api.get(
        `/public/get_stats_public?token=${token}&employee_id=${employee.id}&month=${selectedMonth}&year=${selectedYear}`
      );
      if (response.status === 200) {
        setStatsData(response.data.stats);
        setAbsentDates(response.data.absentDates || []);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "Failed to load stats");
      setTimeout(() => setErrorMsg(""), 4000);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleMonthChange = async (newMonth, newYear) => {
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
    if (selectedEmployee) {
      setStatsLoading(true);
      try {
        const response = await api.get(
          `/public/get_stats_public?token=${token}&employee_id=${selectedEmployee.id}&month=${newMonth}&year=${newYear}`
        );
        if (response.status === 200) {
          setStatsData(response.data.stats);
          setAbsentDates(response.data.absentDates || []);
        }
      } catch (error) {
        setErrorMsg(error.response?.data?.message || "Failed to load stats");
      } finally {
        setStatsLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f7fa" }}>
        <div style={{ textAlign: "center" }}>
          <div className="loading-spinner"></div>
          <p style={{ marginTop: "15px", color: "#64748b" }}>Verifying link...</p>
        </div>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f7fa", padding: "20px" }}>
        <div style={{ textAlign: "center", background: "white", padding: "60px 40px", borderRadius: "20px", boxShadow: "0 10px 40px rgba(0,0,0,0.1)", maxWidth: "500px" }}>
          <div style={{ fontSize: "72px", marginBottom: "16px", color: "var(--primary)" }}><FaLock /></div>
          <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#1e293b", marginBottom: "8px" }}>Access Denied</h2>
          <p style={{ color: "#64748b", fontSize: "14px" }}>{errorMsg || "This link is invalid or has been revoked."}</p>
        </div>
      </div>
    );
  }

  const filteredEmployees = employees.filter(emp =>
    emp.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employee_fields?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const availableYears = [currentDate.getFullYear(), currentDate.getFullYear() - 1];

  return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa", padding: "20px" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: "20px",
          padding: "30px 40px",
          marginBottom: "24px",
          color: "white"
        }}>
          <h1 style={{ fontSize: "28px", fontWeight: "700", margin: "0 0 8px 0" }}>Employee Statistics</h1>
          <p style={{ margin: 0, opacity: 0.9, fontSize: "14px" }}>Click on any employee to view their monthly attendance statistics</p>
        </div>

        {errorMsg && <div className="error-message" style={{ marginBottom: "20px" }}><FaTimesCircle style={{ marginRight: "6px" }} /> {errorMsg}</div>}

        {/* Search */}
        <div style={{
          background: "white",
          borderRadius: "16px",
          padding: "20px",
          marginBottom: "20px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
        }}>
          <div className="search-box" style={{ maxWidth: "400px" }}>
            <span className="search-icon"><FaSearch /></span>
            <input
              type="text"
              placeholder="Search by name or field..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Employee List */}
        <div style={{
          background: "white",
          borderRadius: "16px",
          padding: "20px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
        }}>
          <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#1e293b", marginBottom: "16px" }}>
            Employees ({filteredEmployees.length})
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "12px" }}>
            {filteredEmployees.map((emp) => (
              <div
                key={emp.id}
                onClick={() => handleEmployeeClick(emp)}
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  padding: "16px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#eef2ff";
                  e.currentTarget.style.borderColor = "#667eea";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f8fafc";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "700",
                  fontSize: "16px",
                  flexShrink: 0
                }}>
                  {emp.employeeName?.charAt(0) || "E"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: "600", color: "#1e293b", fontSize: "14px", marginBottom: "4px" }}>
                    {emp.employeeName}
                  </div>
                  <div style={{ fontSize: "11px", color: "#64748b", textTransform: "capitalize" }}>
                    {emp.role} • {emp.employee_fields || "—"}
                  </div>
                </div>
                <div style={{ fontSize: "18px", color: "#667eea" }}>→</div>
              </div>
            ))}
          </div>
          {filteredEmployees.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
              No employees found
            </div>
          )}
        </div>
      </div>

      {/* STATS MODAL */}
      {showStatsModal && selectedEmployee && (
        <div className="modal-overlay" onClick={() => setShowStatsModal(false)}>
          <div className="modal-container stats-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <div>
                <h3 className="modal-title">Attendance Statistics</h3>
                <p className="modal-subtitle">
                  {selectedEmployee.employeeName}'s Monthly Overview
                </p>
              </div>
              <button className="modal-close" onClick={() => setShowStatsModal(false)} aria-label="Close stats modal"><FaTimes /></button>
            </div>
            <div className="modal-body-custom">
              {/* Month/Year Selector */}
              <div style={{ marginBottom: "12px", display: "flex", gap: "10px", marginTop: "-13px" }}>
                <select
                  className="form-input filter-select"
                  value={selectedMonth}
                  onChange={(e) => handleMonthChange(parseInt(e.target.value), selectedYear)}
                  style={{ flex: 1 }}
                >
                  {monthNames.map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select
                  className="form-input filter-select"
                  value={selectedYear}
                  onChange={(e) => handleMonthChange(selectedMonth, parseInt(e.target.value))}
                  style={{ flex: 1 }}
                >
                  {availableYears.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              {statsLoading ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <div className="loading-spinner"></div>
                </div>
              ) : statsData ? (
                <div className="stats-modal-content">
                  <div className="monthly-summary" style={{ background: "linear-gradient(135deg, #eef2ff 0%, #f0f4ff 100%)", borderRadius: "12px", padding: "20px", marginBottom: "12px" }}>
                    <div className="summary-cards">
                      <div className="summary-card">
                        <span className="summary-label">Working Days</span>
                        <span className="summary-value">{statsData.totalWorkingDaysInMonth} days</span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Total Expected Hours</span>
                        <span className="summary-value">{formatHoursMins(statsData.totalExpectedMonthMinutes)}</span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Leave Days</span>
                        <span className="summary-value" style={{ color: '#8b5cf6' }}>
                          {statsData.totalLeaveDaysInMonth || 0} days
                        </span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Daily Expected</span>
                        <span className="summary-value">{formatDuration(statsData.expectedDailyMinutes)}</span>
                      </div>
                    </div>
                    <div className="summary-cards" style={{ marginTop: "12px" }}>
                      <div className="summary-card">
                        <span className="summary-label">Days Worked / Total</span>
                        <span className="summary-value">
                          <span style={{ color: "#d97706" }}>{statsData.workingDaysPassed - statsData.totalAbsentDays}</span> / {statsData.totalWorkingDaysInMonth}
                        </span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Actual Hours / Expected</span>
                        <span className="summary-value">
                          <span style={{ color: "#d97706" }}>{formatHoursMins(statsData.totalWorkedMinutes)}</span> / {formatHoursMins(statsData.currentExpectedMinutes)}
                        </span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Office Outside Time</span>
                        <span className="summary-value" style={{ color: statsData.totalPauseMinutesMonth > 0 ? '#f59e0b' : '#1e293b' }}>
                          {formatHoursMins(statsData.totalPauseMinutesMonth)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* TIME BREAKDOWN */}
                  <div className="summary-card" style={{ marginTop: "12px", background: "linear-gradient(135deg, #f8fafc, #f1f5f9)", borderRadius: "12px", padding: "20px", border: "2px solid #e2e8f0" }}>
                    <span className="summary-label" style={{ marginBottom: "10px", textAlign: "center", color: '#475569', display: "block" }}>
                      ⏱️ Time Breakdown
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      {(() => {
                        const absentTime = (statsData.totalAbsentDays || 0) * statsData.expectedDailyMinutes;
                        const leaveDaysTime = (statsData.totalLeaveDaysInMonth || 0) * statsData.expectedDailyMinutes;
                        const lateTime = statsData.totalLateMinutes || 0;
                        const earlyDepTime = statsData.totalEarlyDepartureMinutes || 0;
                        const extraTime = statsData.totalExtraTimeMinutes || 0;
                        return (
                          <>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#ef4444', fontFamily: 'monospace' }}>{formatHoursMins(absentTime)}</div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', marginTop: '2px' }}>Absent</div>
                            </div>
                            <span style={{ color: '#cbd5e1', fontSize: '18px' }}>+</span>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#8b5cf6', fontFamily: 'monospace' }}>{formatHoursMins(leaveDaysTime)}</div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', marginTop: '2px' }}>Leave</div>
                            </div>
                            <span style={{ color: '#cbd5e1', fontSize: '18px' }}>+</span>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#f59e0b', fontFamily: 'monospace' }}>{formatHoursMins(lateTime)}</div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', marginTop: '2px' }}>Late</div>
                            </div>
                            <span style={{ color: '#cbd5e1', fontSize: '18px' }}>+</span>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#f97316', fontFamily: 'monospace' }}>{formatHoursMins(earlyDepTime)}</div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', marginTop: '2px' }}>Early Dep.</div>
                            </div>
                            <span style={{ color: '#cbd5e1', fontSize: '18px' }}>-</span>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#10b981', fontFamily: 'monospace' }}>{formatHoursMins(extraTime)}</div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', marginTop: '2px' }}>Extra</div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* ABSENT DATES */}
                  {absentDates.length > 0 && (
                    <div style={{ marginTop: "12px", background: "#fef2f2", borderRadius: "12px", padding: "16px", border: "1px solid #fecaca" }}>
                      <span className="summary-label" style={{ color: '#dc2626', marginBottom: "10px", display: "block" }}>
                        <FaExclamationTriangle style={{ marginRight: "6px" }} /> Absent Days ({absentDates.length})
                      </span>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {absentDates.map((d, i) => (
                          <span key={i} style={{
                            background: "white",
                            border: "1px solid #fecaca",
                            borderRadius: "6px",
                            padding: "4px 10px",
                            fontSize: "12px",
                            color: "#dc2626",
                            fontWeight: "600"
                          }}>
                            {d.dayName} {d.day}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                  No data available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicStats;